import { Card, Flex, Stack, useTheme, spacing, fontSize } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import { DeleteProjectButton } from './DeleteProjectButton';
import type { Project } from '../../../types/media-sim';

interface ProjectListProps {
  projects: Project[];
  onOpenProject: (id: string) => void;
  /**
   * The current user's Cognito sub, used to decide whether to offer Delete.
   * The API allows the creator or an admin only, so the button is hidden
   * otherwise rather than shown and then rejected with a 403.
   */
  currentUserSub?: string | null;
  isAdmin?: boolean;
  /** Called after a successful delete so the page can refresh. */
  onProjectDeleted?: () => void;
}

/** Human-readable creator: prefer resolved name, then email, then a dash. */
function creatorLabel(project: Project): string {
  return project.creator_name || project.creator_email || '—';
}

export function ProjectList({
  projects,
  onOpenProject,
  currentUserSub,
  isAdmin = false,
  onProjectDeleted,
}: ProjectListProps) {
  const { theme } = useTheme();

  const canDelete = (project: Project) =>
    isAdmin || (!!currentUserSub && project.created_by === currentUserSub);

  if (projects.length === 0) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" style={{ padding: spacing.lg }}>
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
              No projects found. Create a project to get started.
            </p>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const labelStyle = { fontSize: fontSize.xs, color: mutedColor, fontWeight: 600 as const };
  const valueStyle = { fontSize: fontSize.sm, color: textColor };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: spacing.md,
    }}>
      {projects.map((project) => (
        <Card
          key={project.id}
          variant="elevated"
          style={{ cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }}
          onClick={() => onOpenProject(project.id)}
        >
          <Card.Body>
            <Stack gap="sm">
              <Flex justify="between" align="start" gap="sm">
                <h3 style={{
                  margin: 0,
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: theme.colors.primary[700],
                  lineHeight: 1.3,
                }}>
                  {project.project_name}
                </h3>
                <StatusBadge status={project.status} size="sm" />
              </Flex>

              <Stack gap="xs">
                <Flex gap="xs" align="center">
                  <span style={labelStyle}>Client:</span>
                  <span style={valueStyle}>{project.client_name || '—'}</span>
                </Flex>
                <Flex gap="xs" align="center">
                  <span style={labelStyle}>Created by:</span>
                  <span style={valueStyle}>{creatorLabel(project)}</span>
                </Flex>
                <Flex gap="xs" align="center">
                  <span style={labelStyle}>Created:</span>
                  <span style={valueStyle}>
                    {new Date(project.created_at).toLocaleDateString()}
                  </span>
                </Flex>
              </Stack>

              {canDelete(project) && (
                <Flex justify="end">
                  <DeleteProjectButton
                    projectId={project.id}
                    projectName={project.project_name}
                    onDeleted={onProjectDeleted}
                  />
                </Flex>
              )}
            </Stack>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}
